import clsx from 'clsx';
import { Ref, forwardRef, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BraSizeSourcesSorted,
  ClientTextReviewResponse,
  HeightUnit,
  MultiOptionQuestion,
  ProductWidget,
  SkinConcernAnswer,
  SkinConcernsQuestion,
  hairToneTitle,
  skinSensitivityTitle,
  skinToneTitle,
  skinTypeTitle,
  skinUndertoneTitle,
  titleForSkinConcern,
} from '@vestico/model';

import { UserContext, braSizeString, heightString, sizeString, weightMatchPercentageString } from '@vestico/shared';

import { ProductsList } from './ProductsList';
import { ReviewContentItem } from './ReviewContentItem';
import { ReviewContentItemContentReviewText } from './ReviewContentItemContentReviewText';
import style from './ReviewContentMatched.module.css';

export interface MatchedReviewContentProps {
  response: ClientTextReviewResponse;
  products: ProductWidget[] | null | undefined;
  showProducts?: boolean;
  onProductLinkClick?: (product: ProductWidget) => void;
}

export const MatchedReviewContent = forwardRef(
  ({ response, products, showProducts, onProductLinkClick }: MatchedReviewContentProps, ref: Ref<HTMLDivElement>) => {
    const { reviewText, reviewTextLanguage, postDocumentID } = response;
    const { user } = useContext(UserContext);

    const { t } = useTranslation(['translation', 'model', 'shared']);

    const braSource = useMemo(() => {
      if (!user?.braSizeSourceId) {
        return null;
      }
      for (const source of BraSizeSourcesSorted()) {
        if (source.id === user.braSizeSourceId) {
          return source;
        }
      }
      return null;
    }, [user]);

    const height = heightString(response, user?.heightUnit ?? HeightUnit.centimeter);
    const weightMatch = weightMatchPercentageString(response);
    const braSize = braSizeString(response, braSource);

    const { products: responseProducts, skinTone, skinUndertone, skinType, skinSensitivity, hairTone } = response;

    // Only show size,variant worn, usual size if it's a single product post
    const responseHasProducts = useMemo(() => responseProducts.length > 0, []);
    const singleResponseProductInfo = useMemo(() => (responseProducts.length === 1 ? responseProducts[0] : undefined), [responseProducts]);
    const size = useMemo(
      () => (singleResponseProductInfo?.size ? sizeString(singleResponseProductInfo.size) : undefined),
      [singleResponseProductInfo]
    );
    const variantWorn = useMemo(
      () => (singleResponseProductInfo?.variantName ? sizeString(singleResponseProductInfo?.variantName) : undefined),
      [singleResponseProductInfo]
    );
    const usualSize = useMemo(
      () => (singleResponseProductInfo && response.usualSizeAnswer ? sizeString(response.usualSizeAnswer) : undefined),
      [singleResponseProductInfo, response.usualSizeAnswer]
    );

    const createContentForMultiSelectQuestion = useCallback((question: MultiOptionQuestion, values: SkinConcernAnswer[] | undefined) => {
      if (!values || values.length === 0) {
        return null;
      }

      const titles = values.map((value) => titleForSkinConcern(value));
      return titles;
    }, []);

    const skinConcernAnswersContent = useMemo(
      () => createContentForMultiSelectQuestion(SkinConcernsQuestion, response.skinConcernAnswers),
      [createContentForMultiSelectQuestion, response.skinConcernAnswers]
    );

    const skinToneString = skinTone ? skinToneTitle(skinTone, t) : undefined;
    const skinUndertoneString = skinUndertone ? skinUndertoneTitle(skinUndertone, t) : undefined;
    const skinTypeString = useMemo(() => (skinType ? skinTypeTitle(skinType, t) : undefined), [skinType, t]);
    const skinSensitiveString = useMemo(() => (skinSensitivity ? skinSensitivityTitle(skinSensitivity, t) : undefined), [skinSensitivity, t]);
    const combinedSkinSensitiveString = useMemo(() => {
      if (!skinSensitiveString) {
        return skinTypeString;
      }
      if (!skinTypeString) {
        return skinSensitiveString;
      }
      return [skinTypeString, skinSensitiveString];
    }, [skinSensitiveString, skinTypeString]);

    const hairToneString = hairTone ? hairToneTitle(hairTone, t) : undefined;

    return (
      <div className={clsx(style.root)} ref={ref}>
        <ReviewContentItem title={t('LOC_REVIEW_CONTENT_TITLE_SHADE_WORN')} content={variantWorn} />
        <ReviewContentItem
          title={t('LOC_REVIEW_CONTENT_TITLE_SKIN_TONE')}
          content={skinToneString && skinUndertoneString ? [skinToneString, skinUndertoneString] : undefined}
        />
        <ReviewContentItem title={t('LOC_REVIEW_CONTENT_TITLE_SKIN_TYPE')} content={combinedSkinSensitiveString} />
        <ReviewContentItem title={t('LOC_REVIEW_CONTENT_TITLE_HAIR_TONE')} content={hairToneString} />
        <ReviewContentItem title={t('LOC_REVIEW_CONTENT_TITLE_SIZE_WORN')} content={size} />
        <ReviewContentItem title={t('LOC_REVIEW_CONTENT_TITLE_USUAL_WEARS')} content={usualSize} />
        <ReviewContentItem title={t('LOC_REVIEW_CONTENT_TITLE_HEIGHT')} content={height} />
        <ReviewContentItem title={t('LOC_REVIEW_CONTENT_TITLE_WEIGHT')} content={weightMatch} />
        <ReviewContentItem title={t('LOC_REVIEW_CONTENT_TITLE_BUST_SIZE')} content={braSize} />
        <ReviewContentItem title={t('LOC_REVIEW_CONTENT_TITLE_SKIN_CONCERNS')} content={skinConcernAnswersContent} />
        <ReviewContentItemContentReviewText reviewText={reviewText} reviewTextLanguage={reviewTextLanguage} postDocumentID={postDocumentID} />
        {showProducts && responseHasProducts ? (
          <ReviewContentItem
            title={t('LOC_FEATURED_PRODUCTS', { count: response.products.length })}
            content={<ProductsList response={response} products={products} onProductLinkClick={onProductLinkClick} />}
          />
        ) : null}
      </div>
    );
  }
);
